import PROFESSION_TASK_PAGE_BY_SLUG from "./professionTaskPageBySlug.graphql";
import PROFESSION_TASK_CITY_PAGE from "./professionTaskCityPage.graphql";
import PROFESSION_TASK_SIBLING_TASKS from "./professionTaskSiblingTasks.graphql";
import PROFESSION_TASK_HUB_STATS from "./professionTaskHubStats.graphql";
import {
  HUB_PEOPLE_PAGE_SIZE,
  cityPagePeoplePageSize,
  professionTaskPagePath,
  resolveCityOutcome,
  resolveEmptyHubOutcome,
  resolveHubOutcome,
  resolveSwitchedOffOutcome,
} from "./professionTaskPageOutcome";
import { useFetchProfessionTaskProfessionals } from "./useFetchProfessionTaskProfessionals";
import { permanentRedirect, withRequestQuery } from "~/helpers/redirect";
import { useProfessionNavLinksStore } from "~/stores/professionNavLinks";

function redirectToTaskPage(context, { status, slug, citySeoName }) {
  const location = professionTaskPagePath(context.localePath, {
    slug,
    citySeoName,
  });
  if (status === 301) {
    permanentRedirect({
      res: context.res,
      redirect: context.redirect,
      location,
      requestUrl: context.route?.fullPath,
    });
    return;
  }
  context.redirect(status, withRequestQuery(location, context.route?.fullPath));
}

function setProfessionContext(
  context,
  { profession, city = null, professionTasks = null }
) {
  return Promise.all([
    context.store.dispatch("listPage/setProfession", profession),
    context.store.dispatch("listPage/setProfessionTask", null),
    context.store.dispatch("listPage/setCity", city),
    context.store.dispatch("listPage/setCounty", null),
    useProfessionNavLinksStore(context.$pinia).load(context, {
      professionSeoName: profession.seoName,
      professionTasks,
    }),
  ]);
}

async function loadSiblingTasks(context, professionSeoName) {
  try {
    const { popularProfessionTasksByProfession } = await context.$query(
      PROFESSION_TASK_SIBLING_TASKS,
      { professionSeoName }
    );
    return popularProfessionTasksByProfession ?? [];
  } catch (error) {
    context.$logger.error("Profession task sibling tasks failed", error);
    return [];
  }
}

async function loadHubStats(context, slug) {
  try {
    const { professionTaskPageBySlug } = await context.$query(
      PROFESSION_TASK_HUB_STATS,
      { slug }
    );
    return professionTaskPageBySlug?.page?.stats ?? null;
  } catch (error) {
    context.$logger.error("Profession task hub stats failed", error);
    return null;
  }
}

export async function loadProfessionTaskHubPage(context, slug) {
  const { professionTaskPageBySlug } = await context.$query(
    PROFESSION_TASK_PAGE_BY_SLUG,
    { slug }
  );
  const outcome = resolveHubOutcome(professionTaskPageBySlug);

  if (!outcome) {
    return null;
  }
  if (outcome.redirect) {
    redirectToTaskPage(context, outcome.redirect);
    return { redirected: true };
  }

  const emptyOutcome = resolveEmptyHubOutcome(outcome.page);
  if (emptyOutcome) {
    redirectToTaskPage(context, emptyOutcome.redirect);
    return { redirected: true };
  }

  const [people, stats] = await Promise.all([
    useFetchProfessionTaskProfessionals(context)({
      professionTaskSeoName: outcome.page.professionTask.seoName,
      pageSize: HUB_PEOPLE_PAGE_SIZE,
    }),
    loadHubStats(context, outcome.page.slug),
    setProfessionContext(context, { profession: outcome.page.profession }),
  ]);

  return {
    data: {
      taskHubPage: {
        page: { ...outcome.page, stats },
        people: people?.people ?? [],
      },
    },
  };
}

export async function loadProfessionTaskCountyPage(context, slug) {
  const { professionTaskPageBySlug } = await context.$query(
    PROFESSION_TASK_PAGE_BY_SLUG,
    { slug }
  );
  const page = professionTaskPageBySlug?.page;
  const redirect = page
    ? { status: 301, slug: page.slug }
    : resolveSwitchedOffOutcome(professionTaskPageBySlug)?.redirect;

  if (!redirect) {
    return null;
  }
  redirectToTaskPage(context, redirect);
  return { redirected: true };
}

async function loadSwitchedOffCityRedirect(context, { slug, citySeoName }) {
  const { professionTaskPageBySlug } = await context.$query(
    PROFESSION_TASK_PAGE_BY_SLUG,
    { slug }
  );
  const outcome = resolveSwitchedOffOutcome(
    professionTaskPageBySlug,
    citySeoName
  );
  if (!outcome) {
    return null;
  }
  redirectToTaskPage(context, outcome.redirect);
  return { redirected: true };
}

export async function loadProfessionTaskCityPage(context, { slug, citySeoName }) {
  const { professionTaskPageCity } = await context.$query(
    PROFESSION_TASK_CITY_PAGE,
    { slug, citySeoName }
  );
  const outcome = resolveCityOutcome({ slug, result: professionTaskPageCity });

  if (!outcome) {
    return await loadSwitchedOffCityRedirect(context, { slug, citySeoName });
  }
  if (outcome.redirect) {
    redirectToTaskPage(context, outcome.redirect);
    return { redirected: true };
  }

  const { page, city, stats } = outcome.result;
  const pageSize = cityPagePeoplePageSize(context.$device.isMobile);
  const [people, siblingTasks] = await Promise.all([
    useFetchProfessionTaskProfessionals(context)({
      professionTaskSeoName: page.professionTask.seoName,
      citySeoName: city.seoName,
      pageSize,
    }),
    loadSiblingTasks(context, page.profession.seoName),
  ]);

  const emptyOutcome = resolveCityOutcome({
    slug,
    result: outcome.result,
    peopleTotal: people?.pagination?.total ?? 0,
  });
  if (emptyOutcome.redirect) {
    redirectToTaskPage(context, emptyOutcome.redirect);
    return { redirected: true };
  }

  await setProfessionContext(context, {
    profession: page.profession,
    city,
    professionTasks: siblingTasks,
  });

  return {
    data: {
      taskCityPage: {
        page,
        city,
        stats,
        people: people.people,
        pagination: people.pagination,
        pageSize,
        siblingTasks,
      },
    },
  };
}
